import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Film, Loader2, Check, AlertCircle, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PRODUCT_TYPES } from "@/lib/productTypes";

const STATUS_COPY = {
    extracting: "Reading your video frame-by-frame…",
    deduping: "Removing duplicate shots…",
    generating: "Creating AI model photos for each item…",
    done: "All done!",
    failed: "Something went wrong.",
};

export default function VideoCatalogDialog({ open, onOpenChange, onDone, defaultProductType = "saree", categories = [] }) {
    const [file, setFile] = useState(null);
    const [productType, setProductType] = useState(defaultProductType);
    const [category, setCategory] = useState("");
    const [uploading, setUploading] = useState(false);
    const [job, setJob] = useState(null);
    const [uploadPct, setUploadPct] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setFile(null); setJob(null); setUploading(false); setUploadPct(0); setCategory("");
        } else {
            setProductType(defaultProductType);
        }
    }, [open, defaultProductType]);

    const upload = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("product_type", productType);
            if (category) fd.append("category", category);
            const { data } = await api.post("/admin/video-catalog", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => {
                    if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
                },
            });
            setJob({ job_id: data.job_id, status: data.status });

            // Poll until done/failed (video processing can take several minutes)
            const start = Date.now();
            while (Date.now() - start < 20 * 60 * 1000) {
                await new Promise((r) => setTimeout(r, 4000));
                try {
                    const { data: j } = await api.get(`/admin/video-jobs/${data.job_id}`);
                    setJob(j);
                    if (j.status === "done") {
                        toast.success(`Catalog built: ${j.unique_sarees} item${j.unique_sarees === 1 ? "" : "s"} · AI generating in background`);
                        onDone?.();
                        return;
                    }
                    if (j.status === "failed") {
                        toast.error(j.error || "Video processing failed");
                        return;
                    }
                } catch { /* keep polling */ }
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl flex items-center gap-2">
                        <Film className="w-6 h-6 text-brand-gold" strokeWidth={1.4} />
                        Video → Catalog
                    </DialogTitle>
                </DialogHeader>
                <p className="text-brand-muted text-sm mt-1">
                    Record one video panning over your rack. We'll auto-detect each unique saree, skip duplicates, and generate AI model photos for every design.
                </p>

                {!job ? (
                    <div className="mt-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="ayraa-overline">What's in the video?</label>
                                <select
                                    className="ayraa-input mt-1.5"
                                    value={productType}
                                    onChange={(e) => setProductType(e.target.value)}
                                    data-testid="video-product-type-select"
                                >
                                    {PRODUCT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="ayraa-overline">Catalog row <span className="text-brand-soft text-[10px] tracking-normal">(optional)</span></label>
                                <select
                                    className="ayraa-input mt-1.5"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    data-testid="video-category-select"
                                >
                                    <option value="">— None —</option>
                                    {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <label
                            className="block aspect-video rounded-md border border-dashed border-brand-border bg-brand-subtle/40 grid place-items-center cursor-pointer hover:bg-brand-subtle transition"
                            data-testid="video-dropzone"
                        >
                            <input
                                ref={inputRef}
                                type="file"
                                accept="video/*"
                                className="hidden"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="video-file-input"
                            />
                            {file ? (
                                <div className="text-center px-4">
                                    <Film className="w-6 h-6 mx-auto text-brand-gold" strokeWidth={1.4} />
                                    <div className="text-sm mt-2 font-medium truncate">{file.name}</div>
                                    <div className="text-xs text-brand-muted mt-0.5">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                                </div>
                            ) : (
                                <div className="text-center text-brand-muted">
                                    <Film className="w-7 h-7 mx-auto" strokeWidth={1.4} />
                                    <div className="text-sm mt-2">Tap to choose a video</div>
                                    <div className="text-xs text-brand-soft mt-0.5">MP4 / MOV · up to 300 MB</div>
                                </div>
                            )}
                        </label>
                        <div className="text-xs text-brand-muted leading-relaxed">
                            <strong>Best results:</strong> walk slowly past the rack, pause 2–3 seconds on each saree. Good light, plain wall behind. Prices are optional — edit later from the catalog tab.
                        </div>
                        {uploadPct > 0 && uploadPct < 100 ? (
                            <div className="text-xs text-brand-muted">Uploading… {uploadPct}%</div>
                        ) : null}
                    </div>
                ) : (
                    <div className="mt-6 space-y-4" data-testid="video-progress">
                        <div className="ayraa-card p-4">
                            <div className="flex items-center gap-3">
                                {job.status === "done" ? (
                                    <Check className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                ) : job.status === "failed" ? (
                                    <AlertCircle className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                ) : (
                                    <Loader2 className="w-5 h-5 text-brand-primary animate-spin" strokeWidth={1.5} />
                                )}
                                <div className="flex-1 text-sm font-medium">{STATUS_COPY[job.status] || job.status}</div>
                            </div>
                            {job.error ? <div className="text-xs text-brand-primary mt-2">{job.error}</div> : null}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <Stat label="Frames" value={job.frames_extracted || 0} />
                            <Stat label="Unique" value={job.unique_sarees || 0} />
                            <Stat label="AI started" value={job.ai_generated || 0} />
                        </div>
                        {job.status === "done" ? (
                            <div className="text-xs text-brand-muted">
                                <Sparkles className="w-3 h-3 inline text-brand-gold" strokeWidth={1.5} /> AI photos will land on each saree over the next 1–3 minutes. Refresh the catalog tab.
                            </div>
                        ) : null}
                    </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                    <div className="flex gap-2">
                        <button className="ayraa-btn-outline" onClick={() => onOpenChange(false)} disabled={uploading && !job}>Close</button>
                        {!job ? (
                            <button className="ayraa-btn-primary" onClick={upload} disabled={!file || uploading} data-testid="video-start-btn">
                                <Film className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {uploading ? (uploadPct < 100 ? `Uploading ${uploadPct}%` : "Processing…") : "Build catalog"}
                            </button>
                        ) : job.status === "done" || job.status === "failed" ? (
                            <button className="ayraa-btn-primary" onClick={() => onOpenChange(false)} data-testid="video-close-btn">Done</button>
                        ) : null}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, value }) {
    return (
        <div className="ayraa-card p-3 text-center">
            <div className="ayraa-overline">{label}</div>
            <div className="font-serif text-2xl mt-0.5">{value}</div>
        </div>
    );
}
